import { useState, useRef, useCallback, useEffect } from "react";

export function useGuidedPractice({ speakText, stopSpeech, speechCancelledRef, getPreferredVoice }) {
  const [guidedMode, setGuidedMode] = useState(false);
  const [guidedStep, setGuidedStep] = useState(0);
  const [guidedTimer, setGuidedTimer] = useState(0);
  const [guidedPaused, setGuidedPaused] = useState(false);
  const [guidedComplete, setGuidedComplete] = useState(false);
  const guidedTimerRef = useRef(null);
  const guidedSpeakingRef = useRef(false);

  const getStepDuration = () => 1;

  const stopGuidedPractice = useCallback(() => {
    if (speechCancelledRef) speechCancelledRef.current = true;
    window.speechSynthesis.cancel();
    if (guidedTimerRef.current) clearInterval(guidedTimerRef.current);
    guidedTimerRef.current = null;
    guidedSpeakingRef.current = false;
    setGuidedMode(false);
    setGuidedStep(0);
    setGuidedTimer(0);
    setGuidedPaused(false);
    setGuidedComplete(false);
  }, [speechCancelledRef]);

  const startGuidedPractice = useCallback((instructions) => {
    if (!instructions || !Array.isArray(instructions) || instructions.length === 0) return;
    stopSpeech();
    stopGuidedPractice();
    if (speechCancelledRef) speechCancelledRef.current = false;
    setGuidedMode(true);
    setGuidedStep(0);
    setGuidedTimer(getStepDuration());
    setGuidedPaused(false);
    setGuidedComplete(false);

    guidedSpeakingRef.current = true;
    speakText(`Step 1. ${instructions[0]}`, () => { guidedSpeakingRef.current = false; });

    guidedTimerRef.current = setInterval(() => {
      if (guidedSpeakingRef.current) return;
      setGuidedTimer(prev => {
        if (prev <= 1) return 0;
        return prev - 1;
      });
    }, 1000);
  }, [speakText, stopSpeech, stopGuidedPractice, speechCancelledRef]);

  const toggleGuidedPause = useCallback(() => {
    if (guidedPaused) {
      window.speechSynthesis.resume();
      setGuidedPaused(false);
    } else {
      window.speechSynthesis.pause();
      setGuidedPaused(true);
    }
  }, [guidedPaused]);

  // Auto-advance steps
  useEffect(() => {
    if (!guidedMode || guidedPaused || guidedComplete) return;
    if (guidedTimer > 0) return;
    // Timer hit 0 — advance
  }, [guidedMode, guidedPaused, guidedComplete, guidedTimer]);

  useEffect(() => {
    return () => { if (guidedTimerRef.current) clearInterval(guidedTimerRef.current); };
  }, []);

  // Advance handler (needs instructions passed externally)
  const advanceStep = useCallback((instructions) => {
    if (!guidedMode || guidedPaused || guidedComplete || guidedTimer > 0) return;
    const nextStep = guidedStep + 1;
    if (nextStep >= instructions.length) {
      setGuidedComplete(true);
      if (guidedTimerRef.current) clearInterval(guidedTimerRef.current);
      return true; // session complete
    }
    setGuidedStep(nextStep);
    setGuidedTimer(getStepDuration());
    guidedSpeakingRef.current = true;
    speakText(`Step ${nextStep + 1}. ${instructions[nextStep]}`, () => { guidedSpeakingRef.current = false; });
    return false;
  }, [guidedMode, guidedPaused, guidedComplete, guidedTimer, guidedStep, speakText]);

  return {
    guidedMode, guidedStep, guidedTimer, guidedPaused, guidedComplete,
    startGuidedPractice, stopGuidedPractice, toggleGuidedPause,
    advanceStep, getStepDuration
  };
}
